/**
 * ContextualLinks
 *
 * Global kontextuell länksektion som renderas i slutet av innehållet på
 * alla publika sidor. Syftet är att ingen sida ska ha sina enda inlänkar
 * från mega-meny eller sidfot — de viktas lågt eftersom de finns överallt.
 *
 * Länkarna kommer från linkGraph.ts (ringtopologi + roterade nyckelmål),
 * och redan kurerade länkar på sidan filtreras bort så inget dubbleras.
 */

import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { getGeneratedLinksFor } from "@/lib/seo/linkGraph";
import { getInternalLinksFor } from "@/lib/seo/internalLinks";
import { trackInternalLink } from "@/lib/analytics";
import { primaryPathFor } from "@/lib/seo/canonical-overrides";

const normalize = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : "/");

export function ContextualLinks() {
  const pathname = useRouterState({ select: (s) => normalize(s.location.pathname) });

  const curated = new Set(
    getInternalLinksFor(pathname, { fill: false }).map((l) => l.targetPath),
  );
  const links = getGeneratedLinksFor(pathname)
    .map((l) => ({ ...l, targetPath: primaryPathFor(l.targetPath) }))
    .filter((l) => !curated.has(l.targetPath) && l.targetPath !== pathname);
  if (links.length === 0) return null;

  const isEn = pathname === "/en" || pathname.startsWith("/en/");
  const heading = isEn ? "Continue reading" : "Läs vidare";

  return (
    <section
      aria-label={heading}
      data-aeo="contextual-links"
      className="border-t border-foreground/10 bg-background"
    >
      <div className="mx-auto w-full max-w-6xl px-5 py-12 sm:px-8">
        <p className="mb-5 text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/60">
          {heading}
        </p>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {links.map((l) => (
            <li key={l.targetPath}>
              <Link
                to={l.targetPath}
                onClick={() =>
                  trackInternalLink("related_link_click", {
                    location: "contextual_links",
                    label: l.anchorText,
                    to: l.targetPath,
                    from: pathname,
                  })
                }
                className="group flex items-center justify-between gap-3 rounded-md px-3 py-3 text-sm text-foreground/85 transition hover:bg-foreground/[0.04] hover:text-foreground"
              >
                <span className="truncate">{l.anchorText}</span>
                <ArrowUpRight
                  size={14}
                  className="shrink-0 text-foreground/50 transition group-hover:text-foreground"
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
